import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Banknote, ArrowUpRight, Share2, Flag, Facebook, Instagram } from "lucide-react";

function XLogo({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const shareBtnClass =
  "w-9 h-9 border border-white/15 inline-flex items-center justify-center text-white/70 hover:text-accent hover:border-accent transition-colors";
import { toast } from "sonner";
import { Reveal, ParallaxImage } from "@/components/Motion";
import { useCategories } from "@/context/CategoriesContext";
import TalentPassPanel from "@/components/TalentPass";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/imgSrc";
import { RichBlocks, stripMarks } from "@/components/RichText";
import { usePageMeta } from "@/lib/seo";
import api, { fmtErr } from "@/lib/api";

function Categories({ casting, className = "" }) {
  const { colors } = useCategories();
  if (!(casting.categories || []).length) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="casting-categories">
      {casting.categories.map((cat) => (
        <span
          key={cat}
          style={{ borderColor: colors[cat] || "#888", color: colors[cat] || "#aaa" }}
          className="font-grotesk font-medium text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5"
        >
          {cat}
        </span>
      ))}
    </div>
  );
}

function Facts({ casting, className = "" }) {
  return (
    <div className={`flex flex-col gap-5 ${className}`}>
      <span className="flex items-center gap-3 font-grotesk font-semibold text-sm uppercase tracking-[0.15em] text-white/90">
        <MapPin size={18} className="text-accent" /> {casting.city}
      </span>
      {casting.date && (
        <span className="flex items-center gap-3 font-grotesk font-semibold text-sm uppercase tracking-[0.15em] text-white/90">
          <CalendarDays size={18} className="text-accent" /> {casting.date}
        </span>
      )}
      {casting.compensation && (
        <span className="flex items-center gap-3 font-grotesk font-semibold text-sm uppercase tracking-[0.15em] text-white/90">
          <Banknote size={18} className="text-accent" /> {casting.compensation}
        </span>
      )}
      <span className="font-grotesk font-semibold text-sm uppercase tracking-[0.15em] text-white/90">
        Seeking: {casting.gender || "Any"}{casting.min_height ? ` · ${casting.min_height} cm+` : ""}
      </span>
    </div>
  );
}

function Description({ casting }) {
  return <RichBlocks text={casting.description} />;
}

function HoldPanel() {
  const [busy, setBusy] = useState(false);
  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.top.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };
  return (
    <div className="panel p-8 !border-accent" data-testid="hold-panel">
      <p className="overline-label mb-4 !text-accent">Membership on hold</p>
      <p className="font-display italic text-2xl mb-4">One step and you're back.</p>
      <p className="text-sm text-muted-foreground mb-8">
        We couldn't charge your card, so applications are paused. Update your payment method and your Talent Pass picks up where it left off.
      </p>
      <button data-testid="hold-update-payment" onClick={openPortal} disabled={busy} className="btn-primary w-full disabled:opacity-40">
        {busy ? "Opening…" : "Update payment method"}
      </button>
    </div>
  );
}

function ApplyPanel({ casting, user, talentAccess, applied, message, setMessage, apply, submitting, navigate, onHold }) {
  if (casting.external_url) {
    return (
      <div className="panel p-8" data-testid="open-call-panel">
        <p className="overline-label mb-4 !text-accent">Open call — free to apply</p>
        <p className="font-display italic text-2xl mb-4">Straight to the source.</p>
        <p className="text-sm text-muted-foreground mb-8">
          This call takes applications through its own listing — no membership, no fee. You'll leave Solis Casting.
        </p>
        <a
          data-testid="apply-external-button"
          href={casting.external_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => api.post(`/castings/${casting.id}/outbound`).catch(() => {})}
          className="btn-primary w-full inline-flex items-center justify-center gap-2"
        >
          Apply via the listing <ArrowUpRight size={16} />
        </a>
      </div>
    );
  }
  if (user?.role === "model") {
    if (onHold) return <HoldPanel />;
    if (!talentAccess && casting.talent_pass_required) return <TalentPassPanel />;
    if (applied) {
      return (
        <div className="panel p-8" data-testid="application-confirmation">
          <p className="font-display italic text-3xl mb-2">You're in the room.</p>
          <p className="text-sm text-muted-foreground">Your application has been sent to {casting.brand}. Track it from your dashboard.</p>
        </div>
      );
    }
    return (
      <div className="panel p-8" data-testid="apply-panel">
        <p className="overline-label mb-6">Your note to the house</p>
        <textarea
          data-testid="apply-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder="Availability, experience, why this call…"
          className="field resize-none !border !border-white/20 !p-4 mb-6"
        />
        <button data-testid="apply-casting-button" onClick={apply} disabled={submitting} className="btn-primary w-full disabled:opacity-40">
          {submitting ? "Sending…" : "Apply to this casting"}
        </button>
      </div>
    );
  }
  return (
    <div className="panel p-8">
      <p className="font-display italic text-2xl mb-6">Models only beyond this point.</p>
      <button
        data-testid="apply-login-cta"
        onClick={() => navigate("/auth?mode=register&role=model")}
        className="btn-primary w-full"
      >
        Join as a model to apply
      </button>
    </div>
  );
}

function InstagramApply({ casting, user }) {
  if (!casting.instagram_dm) return null;
  const handle = casting.instagram_dm;
  const applyLine =
    `Hi! I'm applying for "${casting.title}" via Solis Casting.` +
    (user?.slug ? ` My portfolio: ${window.location.origin}/models/${user.slug}` : "");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(applyLine);
      toast.success("Copied — paste it into the DM.");
    } catch {
      toast.error("Couldn't copy — write to @" + handle + " directly.");
    }
  };
  return (
    <div className="panel p-8 mt-6" data-testid="apply-instagram-panel">
      <p className="overline-label mb-4 !text-accent">Prefer the DMs?</p>
      <p className="font-display italic text-2xl mb-4">Apply through Instagram.</p>
      <p className="text-sm text-muted-foreground mb-6">
        This house also takes applications by Instagram DM. The button opens a chat with @{handle} — and copies a
        ready-made line{user?.slug ? " with your portfolio link" : ""} so your message has everything they need.
      </p>
      <a
        data-testid="apply-instagram-button"
        href={`https://ig.me/m/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={copy}
        className="btn-outline w-full inline-flex items-center justify-center gap-2"
      >
        <Instagram size={15} /> Apply via Instagram DM
      </a>
      <button
        data-testid="apply-instagram-copy"
        onClick={copy}
        className="w-full mt-3 font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/50 hover:text-white transition-colors"
      >
        Copy the application line
      </button>
    </div>
  );
}

function SimilarCalls({ similar }) {
  if (!similar.length) return null;
  return (
    <div className="px-6 sm:px-10">
      <section className="mt-32" data-testid="similar-castings">
        <Reveal>
          <p className="overline-label mb-4">Keep Looking</p>
          <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight mb-16">
            Similar <span className="italic text-accent">Calls</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {similar.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <Link to={`/castings/${c.slug || c.id}`} data-testid={`similar-card-${c.id}`} className="group block bg-background h-full">
                {c.image_url ? (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={imgSrc(c.image_url)}
                      alt={c.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] flex flex-col justify-center px-6 border-b border-white/10">
                    <span className="inline-block w-2 h-2 bg-accent mb-3" />
                    <p className="font-display font-light text-2xl leading-tight group-hover:text-accent transition-colors">{c.title}</p>
                  </div>
                )}
                <div className="p-5">
                  {c.image_url && (
                    <div className="flex items-end justify-between gap-3">
                      <p className="font-display text-2xl leading-tight">{c.title}</p>
                      <ArrowUpRight size={18} className="shrink-0 text-white/30 group-hover:text-accent transition-colors" />
                    </div>
                  )}
                  <p className="overline-label !text-[11px] !font-medium !text-white/75 mt-3">{c.brand} · {c.city}</p>
                  <p className="overline-label !text-[11px] !font-semibold !text-white/85 mt-1">{c.compensation || "Rate TBD"}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export default function CastingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [casting, setCasting] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [message, setMessage] = useState("");
  const [applied, setApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    api.get(`/castings/${id}`).then((r) => setCasting(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    if (!casting) return;
    api.get("/castings").then((r) => {
      const others = (r.data || []).filter((c) => c.id !== casting.id);
      const cats = casting.categories || [];
      const sameCat = others.filter((c) => (c.categories || []).some((x) => cats.includes(x)));
      const rest = others.filter((c) => !sameCat.includes(c));
      setSimilar([...sameCat, ...rest].slice(0, 3));
    }).catch(() => {});
  }, [casting]);

  const apply = async () => {
    setSubmitting(true);
    try {
      await api.post(`/castings/${casting.id}/apply`, { message });
      setApplied(true);
      toast.success("Application sent — the house will review your book.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const shareCasting = async () => {
    const shareUrl = `${window.location.origin}/api/share/castings/${casting.slug || casting.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — it unfurls with the cover image on WhatsApp, iMessage and socials.");
    } catch {
      window.prompt("Copy the share link:", shareUrl);
    }
  };

  const shareInstagram = async () => {
    const shareUrl = `${window.location.origin}/api/share/castings/${casting.slug || casting.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it in your Instagram story, bio or DM.");
    } catch {
      window.prompt("Copy the share link:", shareUrl);
    }
  };

  /* Per-page SEO + JobPosting structured data (runs once the casting loads) */
  usePageMeta(casting ? {
    title:
      casting.brand && !casting.title.toLowerCase().includes(casting.brand.toLowerCase())
        ? `${casting.title} — ${casting.brand} | Solis Casting`
        : `${casting.title} | Solis Casting`,
    description:
      stripMarks(casting.description || "").replace(/\s+/g, " ").trim().slice(0, 155) ||
      [casting.brand, casting.city, casting.compensation].filter(Boolean).join(" · "),
    image: casting.image_url ? imgSrc(casting.image_url) : undefined,
    url: `${window.location.origin}/castings/${casting.slug || casting.id}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: casting.title,
      description: stripMarks(casting.description || "").replace(/\s+/g, " ").trim(),
      datePosted: (casting.published_at || casting.created_at || "").slice(0, 10),
      ...(casting.expires_at ? { validThrough: String(casting.expires_at).slice(0, 10) } : {}),
      hiringOrganization: { "@type": "Organization", name: casting.brand || "Solis Casting", sameAs: window.location.origin },
      jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: casting.city || "" } },
      employmentType: "CONTRACTOR",
      identifier: { "@type": "PropertyValue", name: "Solis Casting", value: casting.id },
    },
  } : {});

  /* Sticky mobile apply bar — slides away whenever the real apply panel is on screen */
  const [panelVisible, setPanelVisible] = useState(false);
  useEffect(() => {
    const el = document.querySelector('[data-testid="apply-panel"], [data-testid="open-call-panel"], [data-testid="apply-login-cta"], [data-testid="application-confirmation"]');
    if (!el) return undefined;
    const obs = new IntersectionObserver(([e]) => setPanelVisible(e.isIntersecting), { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [casting, applied, user]);

  const scrollToApply = () => {
    document.querySelector('[data-testid="apply-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => document.querySelector('[data-testid="apply-message-input"]')?.focus({ preventScroll: true }), 500);
  };

  const submitReport = async () => {
    if (!user) { navigate("/auth"); return; }
    if (reportReason.trim().length < 3) { toast.error("Tell the office a little more."); return; }
    setReportBusy(true);
    try {
      await api.post(`/castings/${casting.id}/report`, { reason: reportReason.trim() });
      setReported(true);
      setReportOpen(false);
      toast.success("Reported — the office will review this call.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setReportBusy(false);
    }
  };

  // closed/expired/unknown calls send people straight home, not to a dead end
  if (error) return <Navigate to="/" replace />;
  if (!casting) return <div className="px-6 sm:px-10 py-32 overline-label">Loading…</div>;

  const onHold = !!user?.plan?.on_hold;
  const talentAccess =
    !onHold &&
    (user?.plan?.key === "plan_talent" ||
    (user?.plan?.trial && new Date(user.plan.trial_ends) > new Date()));

  const heroSrc = casting.image_url ? imgSrc(casting.image_url) : null;
  const shareUrl = `${window.location.origin}/api/share/castings/${casting.slug || casting.id}`;
  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const xShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${casting.title} — now casting on Solis Casting`)}&url=${encodeURIComponent(shareUrl)}`;
  const applyProps = { casting, user, talentAccess, applied, message, setMessage, apply, submitting, navigate, onHold };

  /* Editorial Index detail — sticky facts/apply left, portrait + drop-cap description right */
  return (
    <div className="py-24 pb-28 lg:pb-24" data-testid="casting-detail-page">
      <div className="px-6 sm:px-10">
        <Link to="/castings" data-testid="back-to-castings" className="overline-label inline-flex items-center gap-2 hover:text-foreground transition-colors mb-12">
          <ArrowLeft size={14} /> All casting calls
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 px-6 sm:px-10">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <Reveal>
              <p className="overline-label !font-semibold !text-white/85 mb-4">{casting.brand}</p>
              <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mb-10" data-testid="casting-title">
                {casting.title}
              </h1>
            </Reveal>
            <Categories casting={casting} />
            <Facts casting={casting} className="mt-10" />
            <div className="mt-8" data-testid="casting-social-share">
              <p className="overline-label !text-[10px] !text-white/50 mb-3">Share this call</p>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  data-testid="share-facebook-button"
                  href={fbShare}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                  title="Share on Facebook"
                  className={shareBtnClass}
                >
                  <Facebook size={15} />
                </a>
                <a
                  data-testid="share-x-button"
                  href={xShare}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on X"
                  title="Share on X"
                  className={shareBtnClass}
                >
                  <XLogo />
                </a>
                <button
                  data-testid="share-instagram-button"
                  onClick={shareInstagram}
                  aria-label="Share on Instagram"
                  title="Copy link for Instagram"
                  className={shareBtnClass}
                >
                  <Instagram size={15} />
                </button>
                <button
                  data-testid="share-casting-button"
                  onClick={shareCasting}
                  aria-label="Copy share link"
                  title="Copy share link"
                  className={shareBtnClass}
                >
                  <Share2 size={15} />
                </button>
              </div>
            </div>
            <div className="mt-4">
              {!reported && casting.report_enabled !== false ? (
                <button
                  data-testid="report-casting-button"
                  onClick={() => (user ? setReportOpen((v) => !v) : navigate("/auth"))}
                  className="overline-label !text-[10px] !text-white/40 inline-flex items-center gap-2 hover:!text-accent transition-colors"
                >
                  <Flag size={12} /> Report this call
                </button>
              ) : reported ? (
                <span data-testid="report-casting-done" className="overline-label !text-[10px] !text-white/40">
                  Reported — thank you
                </span>
              ) : null}
            </div>
            {reportOpen && (
              <div className="mt-4 panel p-5" data-testid="report-casting-form">
                <p className="overline-label !text-[10px] mb-3">What looks wrong? — goes straight to the office</p>
                <textarea
                  data-testid="report-casting-reason"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="field min-h-[80px]"
                  placeholder="Suspicious fee requests, fake brand, inappropriate demands…"
                />
                <div className="mt-4 flex gap-3">
                  <button data-testid="report-casting-submit" onClick={submitReport} disabled={reportBusy} className="btn-primary !px-6 !py-2.5 disabled:opacity-40">
                    {reportBusy ? "Sending…" : "Send report"}
                  </button>
                  <button onClick={() => setReportOpen(false)} className="btn-outline !px-6 !py-2.5">Cancel</button>
                </div>
              </div>
            )}
            <div className="mt-12"><ApplyPanel {...applyProps} /></div>
            <InstagramApply casting={casting} user={user} />
          </div>
        </div>
        <div className="lg:col-span-8">
          {heroSrc && (
            <Reveal>
              <div className="w-full aspect-[3/4] max-h-[80vh] overflow-hidden border border-white/10 mb-16" data-testid="casting-hero">
                <ParallaxImage src={heroSrc} alt={casting.title} className="w-full h-full" speed={6} />
              </div>
            </Reveal>
          )}
          <Reveal delay={0.1}><Description casting={casting} /></Reveal>
        </div>
      </div>
      <SimilarCalls similar={similar} />

      {/* Sticky apply bar — phones/tablets only; hides while the apply panel is visible */}
      {!applied && (
        <div
          data-testid="mobile-apply-bar"
          className={`fixed bottom-0 inset-x-0 z-40 lg:hidden transition-transform duration-300 ${panelVisible ? "translate-y-full" : "translate-y-0"}`}
        >
          <div className="bg-background/95 backdrop-blur-md border-t border-white/10 px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <p className="font-display text-base truncate">{casting.title}</p>
              <p className="overline-label !text-[9px] !text-accent">{casting.compensation || casting.brand}</p>
            </div>
            {casting.external_url ? (
              <a
                href={casting.external_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => api.post(`/castings/${casting.id}/outbound`).catch(() => {})}
                data-testid="mobile-bar-apply"
                className="btn-primary !py-3 !px-5 shrink-0 inline-flex items-center gap-2"
              >
                Apply free <ArrowUpRight size={14} />
              </a>
            ) : user?.role === "model" && onHold ? (
              <button data-testid="mobile-bar-hold" onClick={scrollToApply} className="btn-primary !py-3 !px-5 shrink-0">
                Update payment
              </button>
            ) : user?.role === "model" && talentAccess ? (
              <button data-testid="mobile-bar-apply" onClick={scrollToApply} className="btn-primary !py-3 !px-5 shrink-0">
                Apply now
              </button>
            ) : (
              <button data-testid="mobile-bar-join" onClick={() => navigate("/auth")} className="btn-primary !py-3 !px-5 shrink-0">
                Join to apply
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
